class ZohUpcomingEvents extends HTMLElement {
  #viewport = null;
  #track = null;
  #prev = null;
  #next = null;
  #nav = null;
  #slides = [];
  #modal = null;
  #galleryTrack = null;
  #galleryPrev = null;
  #galleryNext = null;
  #map = null;
  #index = 0;
  #galleryIndex = 0;
  #galleryCount = 0;
  #isDragging = false;
  #pointerId = null;
  #startX = 0;
  #startY = 0;
  #deltaX = 0;
  #pendingDeltaX = 0;
  #moved = false;
  #axisLocked = null;
  #dragOrigin = 0;
  #dragRafId = null;
  #resizeObserver = null;
  #lastFocus = null;
  #galleryStatus = null;
  #reducedMotionQuery = null;

  connectedCallback() {
    this.#viewport = this.querySelector('[data-zoh-ev-viewport]');
    this.#track = this.querySelector('[data-zoh-ev-track]');
    this.#prev = this.querySelector('[data-zoh-ev-prev]');
    this.#next = this.querySelector('[data-zoh-ev-next]');
    this.#nav = this.querySelector('[data-zoh-ev-nav]');
    this.#slides = Array.from(this.querySelectorAll('[data-zoh-ev-slide]'));
    this.#modal = this.querySelector('[data-zoh-ev-modal]');
    this.#galleryTrack = this.querySelector('[data-zoh-ev-gallery-track]');
    this.#galleryPrev = this.querySelector('[data-zoh-ev-gallery-prev]');
    this.#galleryNext = this.querySelector('[data-zoh-ev-gallery-next]');
    this.#galleryStatus = this.querySelector('[data-zoh-ev-gallery-status]');
    this.#map = this.querySelector('[data-zoh-ev-map]');
    this.#reducedMotionQuery = window.matchMedia('(prefers-reduced-motion: reduce)');

    if (!this.#viewport || !this.#track || this.#slides.length === 0) return;

    this.#prev?.addEventListener('click', this.#onPrev);
    this.#next?.addEventListener('click', this.#onNext);
    this.#viewport.addEventListener('pointerdown', this.#onPointerDown, { passive: true });
    this.#track.addEventListener('click', this.#onTrackClick, true);
    this.#track.addEventListener('keydown', this.#onKeyDown);
    this.querySelectorAll('[data-zoh-ev-open]').forEach((button) => {
      button.addEventListener('click', this.#onOpenModal);
    });
    this.querySelector('[data-zoh-ev-close]')?.addEventListener('click', this.#onCloseModal);
    this.#modal?.addEventListener('cancel', this.#onCloseModal);
    this.#modal?.addEventListener('click', this.#onBackdropClick);
    this.#galleryPrev?.addEventListener('click', this.#onGalleryPrev);
    this.#galleryNext?.addEventListener('click', this.#onGalleryNext);
    window.addEventListener('resize', this.#onResize, { passive: true });
    window.addEventListener('load', this.#onResize, { passive: true });

    if (typeof ResizeObserver !== 'undefined') {
      this.#resizeObserver = new ResizeObserver(() => {
        if (!this.#isDragging) this.#render(false);
      });
      this.#resizeObserver.observe(this.#viewport);
    }

    this.#render(false);
  }

  disconnectedCallback() {
    this.#prev?.removeEventListener('click', this.#onPrev);
    this.#next?.removeEventListener('click', this.#onNext);
    this.#viewport?.removeEventListener('pointerdown', this.#onPointerDown);
    this.#unbindWindowPointer();
    this.#track?.removeEventListener('click', this.#onTrackClick, true);
    this.#track?.removeEventListener('keydown', this.#onKeyDown);
    this.querySelectorAll('[data-zoh-ev-open]').forEach((button) => {
      button.removeEventListener('click', this.#onOpenModal);
    });
    this.querySelector('[data-zoh-ev-close]')?.removeEventListener('click', this.#onCloseModal);
    this.#modal?.removeEventListener('cancel', this.#onCloseModal);
    this.#modal?.removeEventListener('click', this.#onBackdropClick);
    this.#galleryPrev?.removeEventListener('click', this.#onGalleryPrev);
    this.#galleryNext?.removeEventListener('click', this.#onGalleryNext);
    window.removeEventListener('resize', this.#onResize);
    window.removeEventListener('load', this.#onResize);
    this.#resizeObserver?.disconnect();
    if (this.#dragRafId !== null) cancelAnimationFrame(this.#dragRafId);
  }

  #bindWindowPointer() {
    window.addEventListener('pointermove', this.#onPointerMove, { passive: false });
    window.addEventListener('pointerup', this.#onPointerUp, { passive: true });
    window.addEventListener('pointercancel', this.#onPointerUp, { passive: true });
  }

  #unbindWindowPointer() {
    window.removeEventListener('pointermove', this.#onPointerMove);
    window.removeEventListener('pointerup', this.#onPointerUp);
    window.removeEventListener('pointercancel', this.#onPointerUp);
  }

  #prefersReducedMotion() {
    return this.#reducedMotionQuery?.matches === true;
  }

  #getGap() {
    const styles = window.getComputedStyle(this.#track);
    const gap = Number.parseFloat(styles.columnGap || styles.gap);
    return Number.isNaN(gap) ? 16 : gap;
  }

  #getStep() {
    const slide = this.#slides[0];
    const width = slide instanceof HTMLElement ? slide.getBoundingClientRect().width : 280;
    return width + this.#getGap();
  }

  #maxIndex() {
    if (!this.#viewport) return 0;
    const step = this.#getStep();
    if (step <= 0) return 0;
    const overflow = this.#track.scrollWidth - this.#viewport.clientWidth;
    if (overflow <= 2) return 0;
    return Math.max(0, Math.ceil(overflow / step));
  }

  #readCurrentTranslate() {
    if (!(this.#track instanceof HTMLElement)) return 0;
    const matrix = window.getComputedStyle(this.#track).transform;
    if (!matrix || matrix === 'none') return 0;
    if (matrix.startsWith('matrix3d(')) {
      return Number.parseFloat(matrix.slice(9, -1).split(',')[12]) || 0;
    }
    if (matrix.startsWith('matrix(')) {
      return Number.parseFloat(matrix.slice(7, -1).split(',')[4]) || 0;
    }
    return 0;
  }

  #setDragging(isDragging) {
    this.#isDragging = isDragging;
    this.#viewport?.classList.toggle('is-dragging', isDragging);
    if (this.#track instanceof HTMLElement) {
      this.#track.style.transition = isDragging ? 'none' : '';
    }
  }

  #setTranslate(value) {
    if (!(this.#track instanceof HTMLElement)) return;
    this.#track.style.transform = `translate3d(${value}px, 0, 0)`;
  }

  #render(animate = true) {
    if (!this.#track || this.#isDragging) return;

    const maxIndex = this.#maxIndex();
    this.#index = Math.min(this.#index, maxIndex);
    const translate = -this.#index * this.#getStep();
    const shouldAnimate = animate && !this.#prefersReducedMotion();

    if (this.#nav instanceof HTMLElement) {
      const showNav = maxIndex > 0;
      this.#nav.hidden = !showNav;
      this.#nav.setAttribute('aria-hidden', showNav ? 'false' : 'true');
    }

    if (this.#prev instanceof HTMLButtonElement) {
      this.#prev.disabled = this.#index <= 0;
    }
    if (this.#next instanceof HTMLButtonElement) {
      this.#next.disabled = this.#index >= maxIndex;
    }

    if (!shouldAnimate) {
      this.#track.style.transition = 'none';
      this.#setTranslate(translate);
      void this.#track.offsetWidth;
      this.#track.style.transition = '';
      return;
    }

    this.#track.style.transition = '';
    this.#setTranslate(translate);
  }

  #stepBy(dir) {
    if (this.#isDragging) return;
    const maxIndex = this.#maxIndex();
    this.#index = Math.min(maxIndex, Math.max(0, this.#index + dir));
    this.#render(true);
  }

  #onPrev = () => this.#stepBy(-1);
  #onNext = () => this.#stepBy(1);

  #onKeyDown = (event) => {
    if (event.key === 'ArrowLeft') {
      event.preventDefault();
      this.#stepBy(-1);
    } else if (event.key === 'ArrowRight') {
      event.preventDefault();
      this.#stepBy(1);
    }
  };

  #onPointerDown = (event) => {
    if (event.button !== undefined && event.button !== 0) return;
    if (!this.#viewport || !this.#track || this.#maxIndex() <= 0) return;

    this.#pointerId = event.pointerId;
    this.#startX = event.clientX;
    this.#startY = event.clientY;
    this.#deltaX = 0;
    this.#pendingDeltaX = 0;
    this.#moved = false;
    this.#axisLocked = null;
    this.#dragOrigin = this.#readCurrentTranslate();
    this.#setDragging(true);
    this.#setTranslate(this.#dragOrigin);
    this.#bindWindowPointer();
    this.#viewport.setPointerCapture?.(event.pointerId);
  };

  #onPointerMove = (event) => {
    if (!this.#isDragging || event.pointerId !== this.#pointerId) return;

    const dx = event.clientX - this.#startX;
    const dy = event.clientY - this.#startY;

    if (this.#axisLocked === null) {
      if (Math.abs(dx) < 6 && Math.abs(dy) < 6) return;
      if (Math.abs(dy) > Math.abs(dx)) {
        this.#finishDrag(false);
        return;
      }
      this.#axisLocked = 'x';
    }

    if (this.#axisLocked !== 'x') return;
    event.preventDefault();
    this.#pendingDeltaX = dx;
    this.#moved = Math.abs(dx) > 8;

    if (this.#dragRafId !== null) return;
    this.#dragRafId = requestAnimationFrame(() => {
      this.#dragRafId = null;
      if (!this.#isDragging) return;

      const step = this.#getStep();
      const maxTranslate = 0;
      const minTranslate = -this.#maxIndex() * step;
      let next = this.#dragOrigin + this.#pendingDeltaX;

      if (next > maxTranslate) {
        next = maxTranslate + (next - maxTranslate) * 0.2;
      } else if (next < minTranslate) {
        next = minTranslate + (next - minTranslate) * 0.2;
      }

      this.#deltaX = this.#pendingDeltaX;
      this.#setTranslate(next);
    });
  };

  #onPointerUp = (event) => {
    if (!this.#isDragging || event.pointerId !== this.#pointerId) return;
    this.#finishDrag(true);
  };

  #finishDrag(shouldSnap) {
    if (!this.#isDragging) return;

    const deltaX = this.#deltaX;
    const step = this.#getStep();
    const threshold = Math.max(24, step * 0.15);

    if (this.#viewport?.hasPointerCapture?.(this.#pointerId)) {
      this.#viewport.releasePointerCapture(this.#pointerId);
    }

    this.#unbindWindowPointer();
    this.#pointerId = null;
    this.#setDragging(false);

    if (this.#dragRafId !== null) {
      cancelAnimationFrame(this.#dragRafId);
      this.#dragRafId = null;
    }

    if (shouldSnap && Math.abs(deltaX) >= threshold) {
      this.#stepBy(deltaX < 0 ? 1 : -1);
    } else {
      this.#render(true);
    }

    this.#deltaX = 0;
    this.#pendingDeltaX = 0;
    this.#moved = Math.abs(deltaX) > 8;
    this.#axisLocked = null;
  }

  #onTrackClick = (event) => {
    if (!this.#moved) return;
    event.preventDefault();
    event.stopPropagation();
    this.#moved = false;
  };

  #onResize = () => {
    this.#render(false);
  };

  #onOpenModal = (event) => {
    if (this.#moved) return;
    const button = event.currentTarget;
    if (!(button instanceof HTMLElement) || !(this.#modal instanceof HTMLDialogElement)) return;

    this.#lastFocus = button;
    const title = button.getAttribute('data-title') || '';
    const location = button.getAttribute('data-location') || '';
    const date = button.getAttribute('data-date') || '';
    const time = button.getAttribute('data-time') || '';
    const description = button.getAttribute('data-description') || '';
    const mapQuery = button.getAttribute('data-map-query') || location;
    const images = (button.getAttribute('data-images') || '')
      .split('|')
      .map((item) => item.trim())
      .filter(Boolean);

    this.querySelector('[data-zoh-ev-modal-title]')?.replaceChildren(document.createTextNode(title));
    this.querySelector('[data-zoh-ev-modal-location]')?.replaceChildren(document.createTextNode(location));
    this.querySelector('[data-zoh-ev-modal-date]')?.replaceChildren(document.createTextNode(date));
    this.querySelector('[data-zoh-ev-modal-time]')?.replaceChildren(
      document.createTextNode(time ? `TIME: ${time}` : '')
    );
    this.querySelector('[data-zoh-ev-modal-description]')?.replaceChildren(document.createTextNode(description));

    this.#buildGallery(images, title);
    this.#setMap(mapQuery, location);

    document.body.classList.add('zoh-ev-modal-open');
    if (typeof this.#modal.showModal === 'function') {
      this.#modal.showModal();
    } else {
      this.#modal.setAttribute('open', '');
    }

    // Move focus to close control so keyboard users land inside the dialog.
    this.querySelector('[data-zoh-ev-close]')?.focus?.();
  };

  #onCloseModal = (event) => {
    event?.preventDefault?.();
    if (!(this.#modal instanceof HTMLDialogElement)) return;

    if (typeof this.#modal.close === 'function') {
      this.#modal.close();
    } else {
      this.#modal.removeAttribute('open');
    }

    document.body.classList.remove('zoh-ev-modal-open');
    if (this.#map instanceof HTMLIFrameElement) {
      this.#map.removeAttribute('src');
    }
    this.#lastFocus?.focus?.();
  };

  #onBackdropClick = (event) => {
    if (event.target === this.#modal) {
      this.#onCloseModal(event);
    }
  };

  #buildGallery(images, title) {
    if (!(this.#galleryTrack instanceof HTMLElement)) return;

    this.#galleryTrack.replaceChildren();
    this.#galleryIndex = 0;
    this.#galleryCount = images.length;

    if (images.length === 0) {
      const empty = document.createElement('div');
      empty.className = 'zoh-upcoming-events__gallery-slide';
      empty.style.background = '#1a1a1a';
      this.#galleryTrack.appendChild(empty);
      this.#galleryCount = 1;
    } else {
      images.forEach((src, index) => {
        const slide = document.createElement('div');
        slide.className = 'zoh-upcoming-events__gallery-slide';
        const img = document.createElement('img');
        img.src = src;
        img.alt = `${title} image ${index + 1}`;
        img.loading = index === 0 ? 'eager' : 'lazy';
        slide.appendChild(img);
        this.#galleryTrack.appendChild(slide);
      });
    }

    this.#renderGallery(false);
  }

  #renderGallery(animate = true) {
    if (!(this.#galleryTrack instanceof HTMLElement)) return;

    const shouldAnimate = animate && !this.#prefersReducedMotion();
    if (!shouldAnimate) {
      this.#galleryTrack.style.transition = 'none';
    } else {
      this.#galleryTrack.style.transition = '';
    }

    this.#galleryTrack.style.transform = `translate3d(-${this.#galleryIndex * 100}%, 0, 0)`;

    if (!shouldAnimate) {
      void this.#galleryTrack.offsetWidth;
      this.#galleryTrack.style.transition = '';
    }

    if (this.#galleryPrev instanceof HTMLElement) {
      this.#galleryPrev.hidden = this.#galleryCount <= 1;
      this.#galleryPrev.toggleAttribute('disabled', this.#galleryIndex <= 0);
    }
    if (this.#galleryNext instanceof HTMLElement) {
      this.#galleryNext.hidden = this.#galleryCount <= 1;
      this.#galleryNext.toggleAttribute('disabled', this.#galleryIndex >= this.#galleryCount - 1);
    }

    if (this.#galleryStatus instanceof HTMLElement && this.#galleryCount > 0) {
      this.#galleryStatus.textContent = `Image ${this.#galleryIndex + 1} of ${this.#galleryCount}`;
    }
  }

  #onGalleryPrev = () => {
    this.#galleryIndex = Math.max(0, this.#galleryIndex - 1);
    this.#renderGallery(true);
  };

  #onGalleryNext = () => {
    this.#galleryIndex = Math.min(this.#galleryCount - 1, this.#galleryIndex + 1);
    this.#renderGallery(true);
  };

  #setMap(query, location = '') {
    if (!(this.#map instanceof HTMLIFrameElement)) return;

    const place = (location || query || '').trim();
    this.#map.title = place ? `Map of ${place}` : 'Event location map';

    if (!query) {
      this.#map.removeAttribute('src');
      return;
    }
    this.#map.src = `https://maps.google.com/maps?q=${encodeURIComponent(query)}&z=14&output=embed`;
  }
}

if (!customElements.get('zoh-upcoming-events')) {
  customElements.define('zoh-upcoming-events', ZohUpcomingEvents);
}
